import { FC, useMemo } from 'react';
import { Box, Typography } from '@mui/material';

import styles from './TripDateActivities.module.scss';
import { TripDetail, TripStop } from '../../../../../../../types/trip.types';
import { formatDateToLocal, formatDateShort } from '../../../../../../../utils/dateUtils';
import { DateIcon, MarkerPinIcon } from '../../../../../../../components/Icons';

interface TripDateActivitiesProps {
  trip: TripDetail;
  selectedDate: Date;
  onShowStopDetails: (activity: TripStop) => void;
}

const TripDateActivities: FC<TripDateActivitiesProps> = ({ trip, selectedDate, onShowStopDetails }) => {
  const getActivityDate = (activity: TripStop): Date | null => {
    let dateString = '';

    try {
      switch (activity.stop_type) {
        case 'Flight':
          dateString = activity.departure_date_time;
          break;
        case 'Hotel':
          dateString = activity.check_in_date_time;
          break;
        default:
          // @ts-expect-error - other activity types might have different fields
          dateString = activity.start_date_time || activity.departure_date_time;
      }

      if (dateString) {
        return new Date(dateString);
      }
    } catch (error) {
      console.warn('Error parsing activity date:', error);
    }

    return null;
  };

  const getActivityTime = (activity: TripStop): string => {
    let dateString = '';

    try {
      switch (activity.stop_type) {
        case 'Flight':
          dateString = activity.departure_date_time;
          break;
        case 'Hotel':
          dateString = activity.check_in_date_time;
          break;
        default:
          // @ts-expect-error - other activity types might have different fields
          dateString = activity.start_date_time || activity.departure_date_time;
      }

      if (dateString) {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
      }
    } catch (error) {
      console.warn('Error parsing activity time:', error);
    }

    return 'Time TBD';
  };

  const selectedDateActivities = useMemo(() => {
    if (!trip?.trip_stops_by_day) return [];

    const selectedDateKey = formatDateToLocal(selectedDate);
    const matchingActivities: TripStop[] = [];

    try {
      Object.values(trip.trip_stops_by_day).forEach(activities => {
        if (activities && Array.isArray(activities)) {
          activities.forEach(activity => {
            if (activity && activity.id) {
              const activityDate = getActivityDate(activity);
              if (activityDate) {
                const activityDateKey = formatDateToLocal(activityDate);
                if (activityDateKey === selectedDateKey) {
                  matchingActivities.push(activity);
                }
              }
            }
          });
        }
      });
    } catch (error) {
      console.warn('Error processing activities:', error);
    }

    return matchingActivities;
  }, [trip?.trip_stops_by_day, selectedDate]);

  if (selectedDateActivities.length === 0) {
    return null;
  }

  return (
    <Box className={styles.tripDateActivities}>
      <Box className={styles.sectionHeader}>
        <Typography
          variant="h10Semibold"
          className={styles.sectionTitle}
        >
          Upcoming • {formatDateShort(selectedDate)}
        </Typography>
      </Box>

      <Box className={styles.activitiesList}>
        {selectedDateActivities.map(activity => {
              return (
                <Box
                  key={activity.id}
                  className={styles.activityCard}
                  onClick={() => onShowStopDetails(activity)}
                >
                  <Box className={styles.activityContent}>
                    <Typography
                      variant="h10Semibold"
                      className={styles.activityType}
                    >
                      {activity.stop_type}
                    </Typography>

                    {activity.stop_type === 'Flight' && (
                      <>
                        <Box className={styles.activityDetail}>
                          <MarkerPinIcon size={16} />
                          <Typography
                            variant="h12"
                            className={styles.detailLabel}
                          >
                            From: {activity.flight_from}
                          </Typography>
                        </Box>
                        <Box className={styles.activityDetail}>
                          <MarkerPinIcon size={16} />
                          <Typography
                            variant="h12"
                            className={styles.detailLabel}
                          >
                            To: {activity.flight_to}
                          </Typography>
                        </Box>
                        <Box className={styles.activityDetail}>
                          <DateIcon size={16} />
                          <Typography
                            variant="h12"
                            className={styles.detailLabel}
                          >
                            Departure: {formatDateShort(selectedDate)}, {getActivityTime(activity)}
                          </Typography>
                        </Box>
                      </>
                    )}

                    {activity.stop_type === 'Hotel' && (
                      <>
                        <Box className={styles.activityDetail}>
                          <MarkerPinIcon size={16} />
                          <Typography
                            variant="h12"
                            className={styles.detailLabel}
                          >
                            Hotel: {activity.name}
                          </Typography>
                        </Box>
                        {activity.address?.name && (
                          <Box className={styles.activityDetail}>
                            <MarkerPinIcon size={16} />
                            <Typography
                              variant="h12"
                              className={styles.detailLabel}
                            >
                              Address: {activity.address.name}
                            </Typography>
                          </Box>
                        )}
                        <Box className={styles.activityDetail}>
                          <DateIcon size={16} />
                          <Typography
                            variant="h12"
                            className={styles.detailLabel}
                          >
                            Check-in: {formatDateShort(selectedDate)}, from {getActivityTime(activity)}
                          </Typography>
                        </Box>
                      </>
                    )}

                    {activity.stop_type !== 'Flight' && activity.stop_type !== 'Hotel' && (
                      <>
                        <Box className={styles.activityDetail}>
                          <MarkerPinIcon size={16} />
                          <Typography
                            variant="h12"
                            className={styles.detailLabel}
                          >
                            {/** @ts-expect-error */}
                            {activity.name || activity.place || 'Activity'}
                          </Typography>
                        </Box>
                        <Box className={styles.activityDetail}>
                          <DateIcon size={16} />
                          <Typography
                            variant="h12"
                            className={styles.detailLabel}
                          >
                            Start: {formatDateShort(selectedDate)}, {getActivityTime(activity)}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              );
            })}
      </Box>
    </Box>
  );
};

export default TripDateActivities;
